import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { api } from '../services/api';
import { getBranding } from '../config/branding';

interface AIAnalysisItem {
  question_id?: number;
  question_text?: string;
  position?: number;
  content_md: string;
  model: string;
  created_at: string;
}

interface AIAnalysisResponse {
  status: 'ready' | 'pending' | 'not_completed';
  research_status: string;
  language: string | null;
  available_languages: string[];
  language_options: { generated?: string | null; english?: string | null; office?: string | null };
  overview: AIAnalysisItem | null;
  questions: AIAnalysisItem[];
  trends: AIAnalysisItem[];
}

const LANG_LABELS: Record<string, string> = {
  en: 'English', uk: 'Українська', ru: 'Русский', es: 'Español',
  fr: 'Français', de: 'Deutsch', it: 'Italiano', pl: 'Polski',
  pt: 'Português', tr: 'Türkçe', kk: 'Қазақша', uz: 'O‘zbek',
  ka: 'ქართული', az: 'Azərbaycan',
};
const LANG_BADGE: Record<string, string> = {
  uk: 'UA', en: 'EN', ru: 'RU', es: 'ES', fr: 'FR', de: 'DE',
  it: 'IT', pl: 'PL', pt: 'PT', tr: 'TR', kk: 'KZ', uz: 'UZ',
  ka: 'GE', az: 'AZ',
};
const langLabel = (code?: string | null) => code ? (LANG_LABELS[code] || code.toUpperCase()) : '';
const langBadge = (code?: string | null) => code ? (LANG_BADGE[code] || code.toUpperCase()) : '';

const renderMarkdown = (md: string): React.ReactNode => {
  const lines = md.split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  // Unescape backslash-escaped markdown specials (Claude often escapes
  // underscores in identifiers like "Ploom\_Kyiv\_April" so they don't
  // get parsed as italic markers).
  const unescape = (s: string) =>
    s.replace(/\\([_*`#[\](){}\\|>!+\-.~])/g, '$1');

  const inline = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    let pkey = 0;
    while ((m = regex.exec(text)) !== null) {
      if (m.index > last) parts.push(unescape(text.slice(last, m.index)));
      const seg = m[0];
      if (seg.startsWith('**')) parts.push(<strong key={`s${pkey++}`}>{unescape(seg.slice(2, -2))}</strong>);
      else if (seg.startsWith('`')) parts.push(<code key={`s${pkey++}`}>{seg.slice(1, -1)}</code>);
      else parts.push(<em key={`s${pkey++}`}>{unescape(seg.slice(1, -1))}</em>);
      last = m.index + seg.length;
    }
    if (last < text.length) parts.push(unescape(text.slice(last)));
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { i++; continue; }

    if (trimmed.startsWith('### ')) { blocks.push(<h4 key={key++} className="ai-md-h4">{inline(trimmed.slice(4))}</h4>); i++; }
    else if (trimmed.startsWith('## ')) { blocks.push(<h3 key={key++} className="ai-md-h3">{inline(trimmed.slice(3))}</h3>); i++; }
    else if (trimmed.startsWith('# ')) { blocks.push(<h2 key={key++} className="ai-md-h2">{inline(trimmed.slice(2))}</h2>); i++; }
    else if (/^---+$/.test(trimmed)) { blocks.push(<hr key={key++} className="ai-md-hr" />); i++; }
    else if (/^[-*]\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (/^[-*]\s/.test(t)) { items.push(t.replace(/^[-*]\s+/, '')); i++; }
        else if (t === '') {
          let j = i + 1;
          while (j < lines.length && lines[j].trim() === '') j++;
          if (j < lines.length && /^[-*]\s/.test(lines[j].trim())) { i = j; continue; }
          break;
        } else if (items.length && !/^[#|]/.test(t) && !/^\d+[.)]\s/.test(t)) { items[items.length - 1] += ' ' + t; i++; }
        else break;
      }
      blocks.push(<ul key={key++} className="ai-md-list">{items.map((it, idx) => <li key={idx}>{inline(it)}</li>)}</ul>);
    }
    else if (/^\d+[.)]\s/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (/^\d+[.)]\s/.test(t)) { items.push(t.replace(/^\d+[.)]\s+/, '')); i++; }
        else if (t === '') {
          let j = i + 1;
          while (j < lines.length && lines[j].trim() === '') j++;
          if (j < lines.length && /^\d+[.)]\s/.test(lines[j].trim())) { i = j; continue; }
          break;
        } else if (items.length && !/^[#|]/.test(t) && !/^[-*]\s/.test(t)) { items[items.length - 1] += ' ' + t; i++; }
        else break;
      }
      blocks.push(<ol key={key++} className="ai-md-list ai-md-olist">{items.map((it, idx) => <li key={idx}>{inline(it)}</li>)}</ol>);
    }
    else if (trimmed.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const cells = lines[i].trim().slice(1, -1).split('|').map(c => c.trim());
        rows.push(cells);
        i++;
      }
      const filtered = rows.filter(r => !r.every(c => /^:?-+:?$/.test(c)));
      if (filtered.length > 0) {
        const [headerRow, ...bodyRows] = filtered;
        blocks.push(
          <table key={key++} className="ai-md-table">
            <thead><tr>{headerRow.map((c, ci) => <th key={ci}>{inline(c)}</th>)}</tr></thead>
            <tbody>{bodyRows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci}>{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        );
      }
    } else {
      const para: string[] = [];
      while (i < lines.length && lines[i].trim() &&
             !/^[-*]\s/.test(lines[i].trim()) &&
             !/^\d+[.)]\s/.test(lines[i].trim()) &&
             !lines[i].trim().startsWith('#') &&
             !lines[i].trim().startsWith('|') &&
             !/^---+$/.test(lines[i].trim())) {
        para.push(lines[i]);
        i++;
      }
      blocks.push(<p key={key++} className="ai-md-p">{inline(para.join(' '))}</p>);
    }
  }
  return blocks;
};

interface Props {
  /** Full GET URL for the AI analysis payload */
  getUrl: string;
  /** POST URL for translate (omit to disable language switcher) */
  translateUrl?: string;
  /** Show the trigger button only if research status matches (default: COMPLETED) */
  researchStatus: string;
  /** Optional className passed to the trigger button */
  buttonClassName?: string;
  /** Optional label for the trigger button (default: "AI Analysis") */
  buttonLabel?: string;
}

const ResearchAIAnalysisPanel: React.FC<Props> = ({
  getUrl, translateUrl, researchStatus,
  buttonClassName = 'dashboard-btn dashboard-btn-ai',
  buttonLabel,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<AIAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<string>('overview');
  const [translating, setTranslating] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const fetchData = async (lang?: string) => {
    const url = lang ? `${getUrl}?lang=${encodeURIComponent(lang)}` : getUrl;
    const response = await api.get(url);
    setData(response.data);
    return response.data as AIAnalysisResponse;
  };

  const openPanel = async () => {
    setOpen(true);
    if (data) return;
    setLoading(true);
    setError(null);
    try {
      const d = await fetchData();
      if (d.overview) setTab('overview');
      else if (d.questions?.length > 0) setTab(`q-${d.questions[0].question_id}`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('research.loadAiFailed'));
    } finally {
      setLoading(false);
    }
  };

  const switchLang = async (target: string) => {
    if (!data || target === data.language) return;
    setError(null);
    if (data.available_languages.includes(target)) {
      setLoading(true);
      try { await fetchData(target); }
      catch (err: any) { setError(err?.response?.data?.detail || t('research.switchLangFailed')); }
      finally { setLoading(false); }
      return;
    }
    if (!translateUrl) return;
    setTranslating(target);
    try {
      const response = await api.post(`${translateUrl}?lang=${encodeURIComponent(target)}`);
      setData(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('research.translationFailed'));
    } finally {
      setTranslating(null);
    }
  };

  const langOpts = (() => {
    if (!data) return [] as { code: string; label: string; tag: string }[];
    const opts = data.language_options || {};
    const out: { code: string; label: string; tag: string }[] = [];
    const seen = new Set<string>();
    const push = (code: string | null | undefined, tag: string) => {
      if (!code) return;
      const c = code.toLowerCase();
      if (seen.has(c)) return;
      seen.add(c);
      out.push({ code: c, label: langLabel(c), tag });
    };
    push(opts.generated, 'original');
    push(opts.english, 'EN');
    push(opts.office, 'office');
    // If translateUrl not provided, only keep languages we already have
    if (!translateUrl) return out.filter(o => data.available_languages.includes(o.code));
    return out;
  })();

  if (researchStatus !== 'COMPLETED') return null;

  return (
    <>
      <button className={buttonClassName} onClick={openPanel} title={t('research.aiAnalysisLower')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l2.39 5.18L20 8.27l-4 3.9.94 5.49L12 15.27l-4.94 2.39L8 12.17l-4-3.9 5.61-1.09L12 2z" />
        </svg>
        {buttonLabel ?? t('research.aiAnalysis')}
      </button>

      {open && createPortal(
        <div className="ai-panel-overlay" onClick={() => setOpen(false)}>
          <div className="ai-panel" onClick={(e) => e.stopPropagation()}>
            <div className="ai-panel-header">
              <div className="ai-panel-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l2.39 5.18L20 8.27l-4 3.9.94 5.49L12 15.27l-4.94 2.39L8 12.17l-4-3.9 5.61-1.09L12 2z" />
                </svg>
                <span>{t('research.aiAnalysis')}</span>
              </div>
              <div className="ai-panel-header-actions">
                {langOpts.length > 1 && (
                  <div className="ai-panel-lang-switch">
                    {langOpts.map(opt => (
                      <button
                        key={opt.code}
                        className={`ai-panel-lang-btn ${data?.language === opt.code ? 'active' : ''}`}
                        onClick={() => switchLang(opt.code)}
                        disabled={translating !== null || loading}
                        title={opt.label}
                      >
                        {translating === opt.code ? <span className="btn-spinner-sm" /> : langBadge(opt.code)}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  className="ai-panel-icon-btn"
                  onClick={() => window.print()}
                  title={t('research.print')}
                  disabled={!data || data.status !== 'ready'}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" />
                  </svg>
                </button>
                <button className="ai-panel-icon-btn" onClick={() => setOpen(false)} aria-label={t('research.close')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            {loading && (
              <div className="ai-panel-loading">
                <span className="btn-spinner" />
                <span>Loading AI analysis…</span>
              </div>
            )}
            {!loading && error && <div className="ai-panel-empty"><p>{error}</p></div>}
            {!loading && !error && data?.status === 'pending' && (
              <div className="ai-panel-empty"><p>{t('research.aiStillGenerating')}</p></div>
            )}

            {!loading && !error && data && data.status === 'ready' && (
              <>
                <div className="ai-panel-tabs">
                  {data.overview && (
                    <button className={`ai-panel-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
                      {t('research.samplePassport')}
                    </button>
                  )}
                  {data.questions.map((q, qi) => (
                    <button
                      key={`q-${q.question_id}`}
                      className={`ai-panel-tab ${tab === `q-${q.question_id}` ? 'active' : ''}`}
                      onClick={() => setTab(`q-${q.question_id}`)}
                      title={q.question_text}
                    >Q{qi + 1}</button>
                  ))}
                  {data.trends.map((t) => {
                    const qi = data.questions.findIndex(q => q.question_id === t.question_id);
                    return (
                    <button
                      key={`t-${t.question_id}`}
                      className={`ai-panel-tab ai-panel-tab-trend ${tab === `t-${t.question_id}` ? 'active' : ''}`}
                      onClick={() => setTab(`t-${t.question_id}`)}
                      title={`Trend vs prior research: ${t.question_text}`}
                    >Q{qi >= 0 ? qi + 1 : t.position} ↗</button>
                    );
                  })}
                </div>

                <div className="ai-panel-content">
                  <div className="ai-print-active-only">
                    {(() => {
                      let item: AIAnalysisItem | null = null;
                      if (tab === 'overview') item = data.overview;
                      else if (tab.startsWith('q-')) {
                        const qid = parseInt(tab.slice(2));
                        item = data.questions.find(q => q.question_id === qid) || null;
                      } else if (tab.startsWith('t-')) {
                        const qid = parseInt(tab.slice(2));
                        item = data.trends.find(t => t.question_id === qid) || null;
                      }
                      if (!item) return <p className="ai-panel-empty">{t('research.nothingToShow')}</p>;
                      return (
                        <>
                          {item.question_text && <p className="ai-panel-q-context">{item.question_text}</p>}
                          <div className="ai-md">{renderMarkdown(item.content_md)}</div>
                          <div className="ai-panel-footer">{getBranding().brand_name} Engine</div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="ai-print-only">
                    {data.overview && (
                      <div className="ai-print-section">
                        <h3 className="ai-print-section-title">{t('research.samplePassport')}</h3>
                        <div className="ai-md">{renderMarkdown(data.overview.content_md)}</div>
                      </div>
                    )}
                    {data.questions.map((q, qi) => (
                      <div key={`p-q-${q.question_id}`} className="ai-print-section">
                        <h3 className="ai-print-section-title">Question {qi + 1}</h3>
                        {q.question_text && <h2 className="ai-print-section-name">{q.question_text}</h2>}
                        <div className="ai-md">{renderMarkdown(q.content_md)}</div>
                      </div>
                    ))}
                    {data.trends.map(t => {
                      const qi = data.questions.findIndex(q => q.question_id === t.question_id);
                      return (
                      <div key={`p-t-${t.question_id}`} className="ai-print-section">
                        <h3 className="ai-print-section-title">Trend · Question {qi >= 0 ? qi + 1 : t.position}</h3>
                        {t.question_text && <h2 className="ai-print-section-name">{t.question_text}</h2>}
                        <div className="ai-md">{renderMarkdown(t.content_md)}</div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ResearchAIAnalysisPanel;
